"use client"

import { Check, Copy } from "lucide-react"
import { useCallback, useState } from "react"
import { cn } from "@/utils/cn"

/**
 * Shell-style comments removed for paste-friendly CLI snippets:
 * - drops full lines whose first non-whitespace char is #
 * - strips trailing ` # …` on remaining lines
 */
export function textToCopyWithoutComments(source: string): string {
  const lines = source.replace(/\r\n/g, "\n").split("\n")
  const out: string[] = []
  for (const raw of lines) {
    const trimmedHead = raw.trimStart()
    if (trimmedHead === "") continue
    if (trimmedHead.startsWith("#")) continue
    const withoutInline = raw.replace(/\s+#.*$/, "").trimEnd()
    if (withoutInline.trim() === "") continue
    out.push(withoutInline.trimEnd())
  }
  return out.join("\n").trimEnd()
}

function fallbackCopyToClipboard(value: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const ta = document.createElement("textarea")
    ta.value = value
    ta.style.position = "fixed"
    ta.style.left = "-9999px"
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand("copy")
      resolve()
    } catch (err) {
      reject(err)
    } finally {
      document.body.removeChild(ta)
    }
  })
}

/**
 * Wraps a code block and adds a copy-to-clipboard control. Pass the exact
 * string that should be copied as `text` (may match visible children).
 */
export function CopyCodeBlock({
  text,
  children,
  className,
  buttonClassName
}: {
  text: string
  children: React.ReactNode
  className?: string
  buttonClassName?: string
}) {
  const [copied, setCopied] = useState(false)

  const onCopy = useCallback(async () => {
    const t = textToCopyWithoutComments(text)
    try {
      await navigator.clipboard.writeText(t)
    } catch {
      try {
        await fallbackCopyToClipboard(t)
      } catch {
        return
      }
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }, [text])

  return (
    <div className={cn("relative", className)}>
      <button
        type="button"
        onClick={onCopy}
        className={cn(
          "absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-lg border border-gray-700 bg-gray-900/95 text-gray-400 shadow-sm backdrop-blur transition hover:border-gray-600 hover:bg-gray-800 hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40",
          buttonClassName
        )}
        aria-label={copied ? "Copied" : "Copy to clipboard"}
      >
        {copied ? (
          <Check className="h-4 w-4 text-emerald-400" aria-hidden />
        ) : (
          <Copy className="h-4 w-4" aria-hidden />
        )}
      </button>
      {children}
    </div>
  )
}

/** Multi-line install / dashboard guides: preserves comment styling per line. */
export function CopyableCommandLines({
  lines
}: {
  lines: string[]
}) {
  const text = lines.join("\n")
  return (
    <CopyCodeBlock
      text={text}
      className="rounded-lg border border-gray-800 bg-gray-950"
    >
      <div className="overflow-x-auto px-4 py-3 pr-14 font-mono text-xs text-gray-300">
        {lines.map((line, i) => (
          <div
            key={i}
            className={
              line === "" ? "h-2" : line.startsWith("#") ? "text-gray-500" : ""
            }
          >
            {line !== "" ? line : null}
          </div>
        ))}
      </div>
    </CopyCodeBlock>
  )
}
